import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { auditClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import TimePeriodField, { inclusiveDayCount } from '../../../components/form/TimePeriodField';

const MAX_RANGE_DAYS = 90;

const exportErrorMessage = async (error) => {
  if (error?.response?.status === 403) {
    return 'You do not have permission to generate audit log reports.';
  }

  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.errors?.message || parsed?.message || 'Failed to generate the audit log report.';
    } catch {
      return 'Failed to generate the audit log report.';
    }
  }

  return data?.message || data?.errors?.message || 'Failed to generate the audit log report.';
};

const fileNameFor = (startDate, endDate) => `logs_${startDate}_to_${endDate}.xlsx`;

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const exportRange = async (startDate, endDate) => {
  const { data } = await auditClient.get('/logs/export', {
    params: {
      start_time: `${startDate}T00:00:00.000Z`,
      end_time: `${endDate}T23:59:59.000Z`,
    },
    responseType: 'blob',
  });

  downloadBlob(data, fileNameFor(startDate, endDate));
};

const AuditLogsPage = () => {
  useDocumentTitle('Audit Logs');
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => dayjs().subtract(6, 'day'));
  const [endDate, setEndDate] = useState(() => dayjs());
  const [creating, setCreating] = useState(false);
  const dayCount = inclusiveDayCount(startDate, endDate);
  const rangeInvalid = dayCount === null || dayCount > MAX_RANGE_DAYS;

  const handleExportRange = async (start, end) => {
    setCreating(true);
    try {
      await exportRange(dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD'));
      setDialogOpen(false);
      dispatch(
        showSnackbar({
          message: 'Audit log report downloaded successfully.',
          severity: 'success',
        }),
      );
    } catch (err) {
      dispatch(showSnackbar({ message: await exportErrorMessage(err), severity: 'error' }));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Audit Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track all actions and security events
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => {
            window.setTimeout(() => setDialogOpen(true), 0);
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Export Audit Log
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        disableEnforceFocus
      >
        <DialogTitle>Export Audit Log</DialogTitle>
        <DialogContent>
          <TimePeriodField
            key={dialogOpen ? 'open' : 'closed'}
            startDate={startDate}
            endDate={endDate}
            maxDays={MAX_RANGE_DAYS}
            required
            sx={{ mt: 1 }}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleExportRange(startDate, endDate)}
            disabled={rangeInvalid || creating}
          >
            {creating ? 'Downloading...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;
